/**
 * Roles: named sets of permissions in one application. A NETWORK role is held
 * network-wide; a TEAM role is held on a team or team group.
 */

import { apiFetch, toArray } from '@/services/core/apiFetch'
import { throwApiError } from '@/services/core/apiError'
import { getContext } from '@/services/networks/networksApi'
import { searchParams, statusParams, type ObjectStatus } from '@/services/core/listSearch'
import { sortParams, type ListPage, type ListSort } from '@/services/core/paging'
import { mapValidationWarnings, type ValidationWarning } from '@/services/core/validationWarnings'

const API_BASE = `${import.meta.env.VITE_ADNUNTIUS_API_BASE || 'https://api.dev.adnuntius.com/api'}/v1`
const JSON_HEADERS = { 'Content-Type': 'application/json' }

export interface Role {
  id: string
  name: string
  application?: string
  /** NETWORK roles apply network-wide; TEAM roles are held on a team or team group. */
  scope?: string
  permissions?: string[]
}

/** A role as its own page edits it. */
export interface RoleDetail extends Role {
  description: string
  application: string
  scope: string
  permissions: string[]
  objectState: string
  validationWarnings: ValidationWarning[]
  createTime: string
  updateTime: string
}

export interface RoleInput {
  name: string
  description?: string
  application?: string
  scope?: string
  /** Replaces the whole set when given. */
  permissions?: string[]
  objectState?: string
}

function mapRole(r: Record<string, unknown>): RoleDetail {
  return {
    id: (r.id as string) ?? '',
    name: (r.name as string) ?? '',
    description: (r.description as string) ?? '',
    application: (r.application as string) ?? '',
    scope: (r.scope as string) ?? '',
    permissions: (r.permissions as string[]) ?? [],
    objectState: (r.objectState as string) ?? '',
    validationWarnings: mapValidationWarnings(r.validationWarnings),
    createTime: (r.createTime as string) ?? '',
    updateTime: (r.updateTime as string) ?? '',
  }
}

/** The options the role list page is narrowed by on the server. */
export interface RoleListOptions {
  page?: number
  pageSize?: number
  search?: string
  status?: ObjectStatus
  sort?: ListSort | null
}

async function save(id: string, body: object, failure: string): Promise<RoleDetail> {
  const res = await apiFetch(
    `${API_BASE}/roles/${encodeURIComponent(id)}?context=${getContext()}`,
    {
      method: 'POST',
      headers: JSON_HEADERS,
      body: JSON.stringify(body),
    }
  )
  if (!res.ok) await throwApiError(res, failure)
  return mapRole(await res.json())
}

export const RolesApi = {
  getAll: async (): Promise<Role[]> => {
    const res = await apiFetch(`${API_BASE}/roles?context=${getContext()}&pageSize=100`, {
      headers: JSON_HEADERS,
    })
    if (!res.ok) throw new Error(`Failed to fetch roles (${res.status})`)
    const data = await res.json()
    return (data.results ?? []).map((r: Role) => ({
      id: r.id,
      name: r.name,
      application: r.application,
      scope: r.scope,
      permissions: r.permissions ?? [],
    }))
  },

  /** One page of roles, filtered by search and state on the server. */
  getPage: async ({ page = 1, pageSize = 25, search, status, sort }: RoleListOptions = {}): Promise<
    ListPage<RoleDetail>
  > => {
    const params = new URLSearchParams({
      ...searchParams('roles', search),
      context: getContext(),
      includeHidden: 'false',
      ...statusParams(status),
      ...sortParams(sort, { orderBy: 'name', orderByDirection: 'ASCENDING' }),
      page: String(page),
      pageSize: String(pageSize),
    })
    const res = await apiFetch(`${API_BASE}/roles?${params}`, { headers: JSON_HEADERS })
    if (!res.ok) await throwApiError(res, 'Failed to fetch roles')
    const data = (await res.json()) as { totalCount?: number }
    const results = toArray<Record<string, unknown>>(data).map(mapRole)
    return { results, totalCount: data.totalCount ?? results.length, page, pageSize }
  },

  getById: async (id: string): Promise<RoleDetail> => {
    const res = await apiFetch(
      `${API_BASE}/roles/${encodeURIComponent(id)}?context=${getContext()}`,
      { headers: JSON_HEADERS }
    )
    if (!res.ok) await throwApiError(res, 'Failed to fetch role')
    return mapRole(await res.json())
  },

  create: (id: string, input: RoleInput) => save(id, { id, ...input }, 'Failed to create role'),

  update: (id: string, input: RoleInput) => save(id, input, 'Failed to save role'),
}
